import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Copy,
  Share2,
  Gift,
  UserCircle,
  Phone,
  ArrowRight,
  Lock,
  X,
  TicketPercent,
  PartyPopper,
  Users,
  ChevronDown,
  ChevronUp,
  Coins,
  Mail,
} from "lucide-react";
import toast from "react-hot-toast";
import SummaryApi from "../common";
import { useNavigate } from "react-router-dom";

const ProfileSettings = () => {
  const [userData, setUserData] = useState(null);
  const [referees, setReferees] = useState([]); // New state for referees
  const [showAllReferees, setShowAllReferees] = useState(false); // Toggle state
  const [loading, setLoading] = useState(true);
  const [apiMessage, setApiMessage] = useState(null);
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const navigate = useNavigate();

  const [referralInput, setReferralInput] = useState("");
  const [isApplying, setIsApplying] = useState(false);

  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const parsed = JSON.parse(sessionData);
      return parsed.user_id || parsed.id || parsed.userId || parsed;
    } catch (e) {
      return null;
    }
  }, []);

  // Fetch Referee Details
  const fetchRefereeDetails = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      const response = await fetch(
        SummaryApi.wallet_ref_transaction_by_referral.url,
        {
          method: SummaryApi.wallet_ref_transaction_by_referral.method,
          headers: { "Content-Type": "application/json" },
          // Changed user_id to referral_id as requested
          body: JSON.stringify({ referral_id: userId }),
        },
      );

      const result = await response.json();
      if (result.status) {
        setReferees(result.data || []);
      }
    } catch (err) {
      console.error("Referee fetch error:", err);
    }
  }, [getUserId]);

  const fetchUserProfile = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      setApiMessage("Please login to view your profile");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        SummaryApi.user_active_code_retrieve_by_id.url,
        {
          method: SummaryApi.user_active_code_retrieve_by_id.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        },
      );

      const result = await response.json();
      if (result.data) {
        setUserData(result.data);
      }
      if (result.status === false) {
        setApiMessage(result.message || "No subscription till now");
      }

      // Fetch referees after profile
      await fetchRefereeDetails();
    } catch (err) {
      console.error("Profile fetch error:", err);
      setApiMessage("Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, [getUserId, fetchRefereeDetails]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleApplyReferral = async () => {
    const userId = getUserId();
    if (!referralInput.trim()) {
      toast.error("Please enter a referral code");
      return;
    }

    setIsApplying(true);
    try {
      const response = await fetch(SummaryApi.apply_referral.url, {
        method: SummaryApi.apply_referral.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          referral_code: referralInput.trim().toUpperCase(),
        }),
      });

      const result = await response.json();

      if (result.status === true) {
        toast.success("Referral applied successfully!");
        setReferralInput("");
        fetchUserProfile();
      } else {
        toast.error(result.message || "Invalid referral code");
      }
    } catch (err) {
      toast.error("Failed to apply code. Please try again.");
    } finally {
      setIsApplying(false);
    }
  };

  const referralCodeToShare = userData?.user_code?.trim() || "";
  const hasOwnCode =
    !!referralCodeToShare && referralCodeToShare.toLowerCase() !== "n/a";
  const isReferralAlreadyApplied =
    userData?.referral_code &&
    userData.referral_code.trim() !== "" &&
    userData.referral_code.toLowerCase() !== "n/a";

  const displayedReferees = showAllReferees ? referees : referees.slice(0, 5);

  const handleCopySuccess = () => {
    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-sm w-full bg-white shadow-2xl rounded-[2.5rem] pointer-events-auto flex flex-col p-7 border-b-[6px] border-rose-500`}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-rose-50 p-3.5 rounded-2xl">
              <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center">
                <Check size={20} className="text-white" strokeWidth={3} />
              </div>
            </div>
            <h2 className="text-xl font-black text-slate-800 tracking-tight">
              Code Copied!
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-1 bg-rose-500 hover:bg-rose-600 text-white py-3.5 rounded-2xl font-black uppercase tracking-wider transition-colors shadow-lg shadow-rose-200/30"
            >
              Awesome
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-1 bg-[#F1F3F5] hover:bg-gray-200 text-[#5F6B7A] py-3.5 rounded-2xl font-black uppercase tracking-wider transition-colors"
            >
              Dismiss
            </button>
          </div>
        </motion.div>
      ),
      { position: "top-center", duration: 1000 },
    );
  };

  const copyToClipboard = async () => {
    if (!referralCodeToShare) return;
    try {
      await navigator.clipboard.writeText(referralCodeToShare);
      handleCopySuccess();
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = referralCodeToShare;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      handleCopySuccess();
    }
  };

  // SHARE VIA EMAIL FUNCTION
  const shareViaEmail = () => {
    const subject = encodeURIComponent("Join me on Eveya!");
    const body = encodeURIComponent(
      `Hi!\n\nUse my referral code ${referralCodeToShare} to join Eveya and unlock exclusive rewards.\n\nDownload the app and apply my code during registration!`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rose-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-pink-50 to-[#FFF0F3] py-8 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full shadow-xl mb-4">
            <UserCircle size={36} className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-rose-950 tracking-tight uppercase">
            {userData?.user_name || "Welcome"}
          </h1>
        </motion.div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white/80 rounded-2xl p-5 border border-rose-100 shadow-sm">
            <p className="text-xs font-black uppercase text-rose-400 mb-1">
              Name
            </p>
            <p className="text-xl font-bold text-rose-950">
              {userData?.user_name || "N/A"}
            </p>
          </div>
          <div className="bg-white/80 rounded-2xl p-5 border border-rose-100 shadow-sm">
            <p className="text-xs font-black uppercase text-rose-400 mb-1">
              Mobile
            </p>
            <p className="text-xl font-bold text-rose-950 tracking-wide">
              {userData?.mobile_number
                ? `+91 ${userData.mobile_number}`
                : "N/A"}
            </p>
          </div>
        </div>

        {/* Referral Status Block */}
        <div className="bg-gradient-to-br from-white to-rose-50 rounded-[2.5rem] p-6 sm:p-10 border border-rose-200/60 shadow-xl">
          <div className="flex flex-col items-center text-center">
            {hasOwnCode ? (
              <>
                <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mb-4 border-2 border-rose-200">
                  <Gift size={32} className="text-rose-500" />
                </div>
                <h2 className="text-2xl font-black text-rose-950 uppercase mb-2">
                  Your Referral Code
                </h2>
                <div className="bg-white rounded-2xl px-8 py-5 shadow-sm border border-rose-100 mb-6 w-full max-w-xs">
                  <p className="text-4xl font-black font-mono tracking-widest text-rose-600 uppercase">
                    {referralCodeToShare}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-lg">
                  <button
                    onClick={copyToClipboard}
                    className="flex flex-row sm:flex-col items-center justify-center gap-2 bg-white text-rose-700 px-2 py-2 rounded-2xl font-black uppercase text-[10px] border border-rose-200 hover:bg-rose-50 transition-all shadow-sm"
                  >
                    <Copy size={18} /> <span>Copy</span>
                  </button>
                  <button
                    onClick={() =>
                      window.open(
                        `https://wa.me/?text=Use my Eveya code ${referralCodeToShare}`,
                        "_blank",
                      )
                    }
                    className="flex flex-row sm:flex-col items-center justify-center gap-2 bg-green-500 text-white px-2 py-2 rounded-2xl font-black uppercase text-[10px] hover:bg-green-600 transition-all shadow-sm"
                  >
                    <Share2 size={18} /> <span>WhatsApp</span>
                  </button>
                  <button
                    onClick={shareViaEmail}
                    className="flex flex-row sm:flex-col items-center justify-center gap-2 bg-blue-500 text-white px-2 py-2 rounded-2xl font-black uppercase text-[10px] hover:bg-blue-600 transition-all shadow-sm"
                  >
                    <Mail size={18} /> <span>Email</span>
                  </button>
                </div>
              </>
            ) : isReferralAlreadyApplied ? (
              <div className="w-full max-w-sm">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4 mx-auto border-2 border-green-200">
                  <PartyPopper size={32} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-black text-rose-950 uppercase mb-2">
                  Referral Applied!
                </h2>
                <p className="text-sm text-rose-800 mb-8 font-medium leading-relaxed">
                  Referral code{" "}
                  <span className="font-black text-rose-600">
                    "{userData.referral_code}"
                  </span>{" "}
                  has been applied.
                </p>
                <button
                  onClick={() => navigate("/packages")}
                  className="w-full bg-rose-600 text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg hover:bg-rose-700 transition-all"
                >
                  <Lock size={18} /> Subscribe to Unlock Rewards
                </button>
              </div>
            ) : (
              <div className="w-full max-w-sm">
                <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mb-4 mx-auto border-2 border-rose-100">
                  <TicketPercent size={32} className="text-rose-500" />
                </div>
                <h2 className="text-xl font-black text-rose-950 uppercase mb-1">
                  Have a Referral Code?
                </h2>
                <p className="text-sm text-rose-700 mb-8 font-medium">
                  Apply it now to get rewards!
                </p>
                <div className="space-y-4">
                  <input
                    type="text"
                    value={referralInput}
                    onChange={(e) =>
                      setReferralInput(e.target.value.toUpperCase())
                    }
                    placeholder="ENTER CODE"
                    className="w-full bg-white border-2 border-rose-200 rounded-2xl px-4 py-5 text-center text-2xl font-black tracking-widest uppercase focus:border-rose-500 outline-none transition-all"
                  />
                  <button
                    onClick={handleApplyReferral}
                    disabled={isApplying || !referralInput}
                    className="w-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white py-5 rounded-2xl font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    {isApplying ? "Applying..." : "Apply Code"}
                    {!isApplying && <ArrowRight size={20} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* --- REFEREE HISTORY SECTION --- */}
        {referees.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[2.5rem] border border-rose-100 shadow-xl overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-rose-50 flex items-center justify-between bg-rose-50/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-rose-500 rounded-xl shadow-lg shadow-rose-200">
                  <Users size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-black text-rose-950 uppercase tracking-tight text-sm">
                    Friends Invited
                  </h3>
                  <p className="text-[10px] text-rose-400 font-bold uppercase">
                    Reward History
                  </p>
                </div>
              </div>
              <span className="bg-rose-100 text-rose-600 px-4 py-1.5 rounded-full text-xs font-black">
                {referees.length} Total
              </span>
            </div>

            {/* List of Referees */}
            <div className="divide-y divide-rose-50">
              {displayedReferees.map((ref) => (
                <div
                  key={ref.wallet_ref_transaction_id}
                  className="p-5 flex items-center justify-between hover:bg-rose-50/20 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar with initial */}
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center text-rose-600 font-black text-lg border border-rose-200">
                      {ref.referee_name?.charAt(0) || "U"}
                    </div>
                    <div>
                      <p className="font-bold text-rose-950 capitalize leading-tight">
                        {ref.referee_name}
                      </p>
                      <p className="text-xs text-rose-400 font-bold mt-0.5">
                        +91 {ref.referee_mobile_number}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center justify-end gap-1.5 text-green-600 font-black bg-green-50 px-3 py-1 rounded-lg border border-green-100">
                      <Coins size={14} />
                      <span className="text-sm">+{ref.referral_points}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 uppercase font-bold mt-2 tracking-tighter">
                      {new Date(ref.transaction_date).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {referees.length > 5 && (
              <button
                onClick={() => setShowAllReferees(!showAllReferees)}
                className="w-full py-5 bg-gray-50/50 text-rose-600 font-black uppercase text-xs flex items-center justify-center gap-2 hover:bg-rose-50 transition-all border-t border-rose-50 group"
              >
                {showAllReferees ? (
                  <>
                    Show Fewer Records{" "}
                    <ChevronUp
                      size={18}
                      className="group-hover:-translate-y-0.5 transition-transform"
                    />
                  </>
                ) : (
                  <>
                    View All {referees.length} Referees{" "}
                    <ChevronDown
                      size={18}
                      className="group-hover:translate-y-0.5 transition-transform"
                    />
                  </>
                )}
              </button>
            )}
          </motion.div>
        )}
      </div>

      {/* Subscribe Modal */}
      <AnimatePresence>
        {isSubscribeModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-[2.5rem] p-8 max-w-md w-full relative shadow-2xl text-center"
            >
              <button
                onClick={() => setIsSubscribeModalOpen(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-800"
              >
                <X size={24} />
              </button>
              <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock size={32} className="text-rose-500" />
              </div>
              <h3 className="text-2xl font-black text-rose-950 mb-3 uppercase">
                Unlock Your Benefits
              </h3>
              <p className="text-sm text-rose-800 mb-8">
                Join Eveya as a subscriber to start earning points for every
                friend you invite!
              </p>
              <button
                onClick={() => navigate("/packages")}
                className="w-full bg-rose-500 text-white py-5 rounded-2xl font-black uppercase tracking-wider shadow-lg hover:bg-rose-600"
              >
                View Packages
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileSettings;
